<script setup>
/**
 * A freight shipment's options, kept on the group master's `freight`: the
 * accessorials, the far end's site type, what goes on the BOL, the pickup
 * window and the drivers' instructions - and the quote chosen to book.
 */
import {computed, set} from "vue";
import store from "@/store";

const props = defineProps({
  value: {type: Object, required: true},
  disabled: Boolean,
  // a quote dialog: no pickup or booking details, and the insured value is typed here
  // (on a package it is the package's declared value)
  quoting: Boolean,
  // coming to us: the pickup is at the vendor's, and our dock's defaults do not apply
  inbound: Boolean,
})

// the freight options object itself, edited in place
const opts = computed(() => props.value)

const lists = computed(() => store.state.settings.defaults.freight || {accessorials: [], location_types: [], insurance_categories: [], item_conditions: []})
const locationTypes = computed(() => [
  {value: null, text: 'Commercial (or Residential)'},
  ...lists.value.location_types.map(t => ({value: t, text: t.replace(/_/g, ' ')})),
])

const accessorials = computed(() => opts.value.accessorials || {})
const toggle = (key, on) => {
  set(opts.value, 'accessorials', {...accessorials.value, [key]: on})
}

const pickup = (key, v) => {
  set(opts.value, 'pickup', {...(opts.value.pickup || {}), [key]: v || null})
}

// what a shipment that says nothing gets (System > Configuration > Shipping)
const pickupDefault = computed(() => props.inbound ? {} : (lists.value.pickup || {}))

const insurance = computed(() => opts.value.insurance || {})
const insure = (key, v) => {
  set(opts.value, 'insurance', {...insurance.value, [key]: v === '' ? null : v})
}

const offer = computed(() => opts.value.offer)
const expired = computed(() => offer.value && offer.value.expires && new Date(offer.value.expires) < new Date())
const validUntil = computed(() => offer.value && offer.value.expires && new Date(offer.value.expires).toLocaleString([], {weekday: 'short', hour: 'numeric', minute: '2-digit'}))
</script>

<template>
  <div>
    <b-alert show :variant="expired ? 'warning' : 'info'" v-if="offer && !quoting" class="small">
      <div class="d-flex">
        <strong class="mr-auto">{{ offer.carrier_name }}<template v-if="offer.service_name"> - {{ offer.service_name }}</template></strong>
        <strong>{{ offer.total | money }}</strong>
      </div>
      <div>
        <template v-if="offer.transit_days">{{ offer.transit_days }} day{{ offer.transit_days == 1 ? '' : 's' }} transit</template>
        <b-badge variant="success" class="ml-1" v-if="offer.guaranteed">Guaranteed</b-badge>
        <b-badge variant="info" class="ml-1" v-if="offer.insured_amount">Insured {{ offer.insured_amount | money }}<template v-if="offer.insurance_premium"> ({{ offer.insurance_premium | money }} incl.)</template></b-badge>
        <span class="float-right" v-if="offer.expires">
          {{ expired ? 'Expired - quoted again when booked' : 'Valid until ' + validUntil }}
        </span>
      </div>
    </b-alert>

    <modal-row label="Accessorials">
      <div class="d-flex flex-wrap">
        <b-form-checkbox v-for="a in lists.accessorials" :key="a.value" class="mr-3 mb-1" :disabled="disabled"
                         :checked="!!accessorials[a.value]" @change="(v) => toggle(a.value, v)">{{ a.text }}
        </b-form-checkbox>
      </div>
    </modal-row>

    <modal-row label="Site Type">
      <b-form-select size="sm" v-model="opts.location_type" :options="locationTypes" :disabled="disabled"/>
    </modal-row>

    <modal-row label="Commodity">
      <b-form-input size="sm" v-model="opts.description" maxlength="100" :disabled="disabled"
                    placeholder="On the BOL (default when blank)"/>
    </modal-row>

    <modal-row label="Insurance">
      <div class="d-flex flex-wrap align-items-center">
        <b-input-group size="sm" prepend="$" class="w-auto mr-2 mb-1" v-if="quoting">
          <b-form-input type="number" min="0" step="1" placeholder="insured value" style="max-width: 9em" :disabled="disabled"
                        :value="insurance.insured_value" @change="(v) => insure('insured_value', v)"/>
        </b-input-group>
        <b-form-select size="sm" class="w-auto mr-2 mb-1" :disabled="disabled"
                       :value="insurance.item_condition || null" @change="(v) => insure('item_condition', v)">
          <b-form-select-option :value="null">Condition (carrier default)</b-form-select-option>
          <b-form-select-option v-for="c in lists.item_conditions" :key="c" :value="c">{{ c }}</b-form-select-option>
        </b-form-select>
        <b-form-select size="sm" class="w-auto mb-1" :disabled="disabled"
                       :value="insurance.commodity_category || null" @change="(v) => insure('commodity_category', v)">
          <b-form-select-option :value="null">Category (carrier default)</b-form-select-option>
          <b-form-select-option v-for="c in lists.insurance_categories" :key="c.value" :value="c.value">{{ c.text }}</b-form-select-option>
        </b-form-select>
      </div>
      <small class="text-muted" v-if="!quoting">Insures the declared value; none declared, none bought.</small>
    </modal-row>

    <template v-if="!quoting">
    <modal-row label="Pickup Window">
      <div class="d-flex align-items-center">
        <b-form-input size="sm" type="time" class="w-auto" :disabled="disabled"
                      :value="value.pickup && value.pickup.ready" @change="(v) => pickup('ready', v)"/>
        <span class="mx-2">to</span>
        <b-form-input size="sm" type="time" class="w-auto" :disabled="disabled"
                      :value="value.pickup && value.pickup.close" @change="(v) => pickup('close', v)"/>
      </div>
    </modal-row>

    <modal-row label="Pickup Notes">
      <b-form-input size="sm" v-model="opts.pickup_instructions" maxlength="250" :disabled="disabled"
                    :placeholder="pickupDefault.instructions || ''"/>
    </modal-row>
    <modal-row label="Delivery Notes">
      <b-form-input size="sm" v-model="opts.delivery_instructions" maxlength="250" :disabled="disabled"/>
    </modal-row>
    </template>
  </div>
</template>
